/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  message,
  notification,
  Checkbox,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import currencyFormatter from 'utils/currencyFormatter';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import * as XLSX from 'xlsx';
import API from 'utils/api';
// Asegúrate de importar tu servicio de Plazas
import { getPlazas } from 'api/catalogos/plazas';
import {
  getPercepcionesPlazas,
  postPercepcionPlaza,
  patchPercepcionPlaza,
  deletePercepcionPlaza,
  deletePercepcionesPlazaBulk,
  getPercepcionPlaza,
} from 'api/catalogos/percepcionesPlazas';
import getProcesoEnCursoTerminado from 'api/catalogos/ProcesoEnCursoTerminado';
import { ESTATUS_ACTIVO, ESTATUS_INACTIVO } from 'utils/constants';
import moment from 'moment';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';

const PercepcionImportePlaza = ({ percepcionId, permission, disabled }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const [disableFields, setDisableFields] = useState(false);
  const fileInputRef = useRef(null);
  const formInstances = [form];

  // Se incrementa tras una importación para forzar el remount de <Table> y
  // que su paginación interna regrese a la página 1 -- si no, los registros
  // recién importados (mayor pk, quedan al final del arreglo/última página)
  // quedan fuera de vista aunque `data` ya se haya refrescado.
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  // Polling de la importación en background (mismo patrón que
  // SolicitudesPercepcionesEmpleados.jsx)
  const [isPollingImport, setIsPollingImport] = useState(false);
  const pollingImportRef = useRef(null);
  const descripcionProcesoImportar = `Importar Importe Plaza Percepcion ${percepcionId}`;

  // Polling de la eliminación masiva en background (mismo patrón que la
  // importación de arriba) -- evita disparar un DELETE por cada fila
  // seleccionada, que satura al servidor con selecciones grandes.
  const [isPollingDelete, setIsPollingDelete] = useState(false);
  const pollingDeleteRef = useRef(null);
  const descripcionProcesoEliminar = `Eliminar Importe Plaza Percepcion ${percepcionId}`;

  // Función para cargar los datos de la tabla (filtrados por percepcionId).
  // `masRecientesPrimero` ordena por id descendente para que, tras una
  // importación masiva, los registros recién creados/cerrados (siempre con
  // el pk más alto, ya que el backend nunca edita un registro existente --
  // ver "Continuidad cronológica" en CLAUDE.md) queden al inicio del
  // arreglo en vez de en la última página.
  const fetchData = async ({ masRecientesPrimero = false } = {}) => {
    if (!percepcionId) return;
    setLoading(true);
    try {
      const _data = await getPercepcionesPlazas({ percepcion: percepcionId });
      let formateados = formatReceived(_data);
      if (masRecientesPrimero) {
        formateados = [...formateados].sort((a, b) => (b.id || 0) - (a.id || 0));
      }
      setData(formateados);
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  // Carga inicial de catálogos y datos
  useEffect(() => {
    let mounted = true;
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setCurrentTableData([]);
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [_estados, _plazas] = await Promise.all([
          getEstadosGlobales(),
          API.get('/catalogos/plazas-vista-optimized').then((res) => res.data).catch(() => []),
        ]);

        if (mounted) {
          setEstados(_estados);
          const listaPlazas = _plazas.results || _plazas || [];
          setPlazas(Array.isArray(listaPlazas) ? listaPlazas : []);

          if (percepcionId) {
            await fetchData();
          }
        }
      } catch (err) {
        onError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAll();

    return () => { mounted = false; };
  }, [percepcionId]);

  const onCancel = () => {
    setVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    try {
      const [key] = selectedRowKeys;
      const match = responseData || data.find((item) => item.id === key);
      const _record = await getPercepcionPlaza(match.id);

      if (_record) {
        form.resetFields();
        const formatted = formatReceived(_record);

        // eslint-disable-next-line max-len
        if (formatted.vigencia_inicial) formatted.vigencia_inicial = moment(formatted.vigencia_inicial);
        if (formatted.vigencia_final) formatted.vigencia_final = moment(formatted.vigencia_final);

        // Si la plaza viene como objeto, extraer ID
        if (typeof formatted.plaza === 'object') formatted.plaza = formatted.plaza?.id;

        form.setFieldsValue(formatted);
        setDisableFields(_record?.estados_globales !== ESTATUS_ACTIVO);
        setVisible(true);
      }
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      // Aseguramos enviar el ID de la percepción padre
      values.percepcion = percepcionId;

      // Formatear fechas a string YYYY-MM-DD
      if (values.vigencia_inicial) values.vigencia_inicial = values.vigencia_inicial.format('YYYY-MM-DD');
      if (values.vigencia_final) values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');

      const [key] = selectedRowKeys;
      const saved = await (key ? patchPercepcionPlaza(key, values) : postPercepcionPlaza(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    if (!selectedRowKeys.length) return;

    // Una sola fila: DELETE directo, sin la ceremonia de un proceso en background.
    if (selectedRowKeys.length === 1) {
      try {
        setLoading(true);
        const deleted = await deletePercepcionPlaza(selectedRowKeys[0]);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
        setLoading(false);
      } catch (err) {
        onError(err, setLoading);
      }
      return;
    }

    // Varias filas: un DELETE HTTP por fila saturaría al servidor con selecciones
    // grandes (p.ej. "seleccionar todos" sobre miles de registros). Se manda UNA
    // sola petición y el backend procesa el borrado en background (Celery),
    // mientras el frontend anima el avance vía polling (ver useEffect de abajo).
    try {
      setVisibleAlert(false);
      const idsAEliminar = selectedRowKeys;
      message.loading({
        content: `Eliminando 0 de ${idsAEliminar.length}...`,
        key: 'eliminarBulkImportePlaza',
        duration: 0,
      });
      const resultado = await deletePercepcionesPlazaBulk(percepcionId, idsAEliminar);
      if (resultado?.task_id) {
        setIsPollingDelete(true);
      } else {
        message.destroy('eliminarBulkImportePlaza');
      }
    } catch (err) {
      message.destroy('eliminarBulkImportePlaza');
      onError(err);
    }
  };

  // Polling del borrado masivo en background (mismo patrón que el polling de
  // importación de arriba) -- anima el avance y refresca la tabla al terminar.
  useEffect(() => {
    if (!isPollingDelete) return () => {};

    let intentos = 0;
    const maxIntentos = 340; // máx ~11 min

    pollingDeleteRef.current = setInterval(async () => {
      try {
        intentos += 1;
        const lista = await getProcesoEnCursoTerminado({
          descripcion: descripcionProcesoEliminar,
        });
        const proceso = lista[0];

        if (proceso) {
          message.loading({
            content: proceso.total_registros
              ? `Eliminando ${proceso.registro_en_curso || 0} de ${proceso.total_registros}...`
              : 'Eliminando registros...',
            key: 'eliminarBulkImportePlaza',
            duration: 0,
          });
        }

        const termino = proceso && proceso.procesando === false;
        if (termino || intentos >= maxIntentos) {
          clearInterval(pollingDeleteRef.current);
          pollingDeleteRef.current = null;
          setIsPollingDelete(false);
          message.destroy('eliminarBulkImportePlaza');

          if (termino) {
            notification.success({
              message: 'Eliminación completada',
              description: `Se eliminaron ${proceso.registro_en_curso || 0} registros de Plaza.`,
              placement: 'topRight',
            });
          } else {
            notification.warning({
              message: 'La eliminación sigue en curso',
              description: 'Sigue procesándose en segundo plano; recarga la tabla en unos minutos.',
              placement: 'topRight',
            });
          }

          onCancel();
          await fetchData();
        }
      } catch (_err) {
        // No se interrumpe el polling: el siguiente tick reintenta. Si el
        // error es por sesión expirada, utils/api.js ya avisó y cerró
        // sesión; aquí solo dejamos rastro para diagnóstico.
        // eslint-disable-next-line no-console
        console.error('[polling eliminación masiva importe por plaza] error al consultar estado', _err);
      }
    }, 2000);

    return () => {
      if (pollingDeleteRef.current) {
        clearInterval(pollingDeleteRef.current);
        pollingDeleteRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPollingDelete]);

  const handleOnRowClick = (record) => {
    if (selectedRowKeys.includes(record.id)) {
      setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
      setSelectedRows((prev) => prev.filter((row) => row.id !== record.id));
    } else {
      setSelectedRowKeys((prev) => [...prev, record.id]);
      setSelectedRows((prev) => [...prev, record]);
    }
  };

  const effectiveData = currentTableData.length ? currentTableData : data;

  const handleTableChange = (_pagination, _filters, _sorter, extra) => {
    setCurrentTableData(extra?.currentDataSource || []);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={selectedRowKeys.length > 0 && selectedRowKeys.length === effectiveData.length}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < effectiveData.length}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRowKeys(effectiveData.map((row) => row.id));
            setSelectedRows([...effectiveData]);
          } else {
            setSelectedRowKeys([]);
            setSelectedRows([]);
          }
        }}
      />
    ),
    onSelect: (record, selected) => {
      if (selected) {
        setSelectedRowKeys((prev) => [...prev, record.id]);
        setSelectedRows((prev) => [...prev, record]);
      } else {
        setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
        setSelectedRows((prev) => prev.filter((row) => row.id !== record.id));
      }
    },
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    // Pre-setear valores por defecto si es necesario
    form.setFieldsValue({
      estados_globales: ESTATUS_ACTIVO,
    });
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Plaza',
      dataIndex: 'plaza',
      key: 'plaza',
      width: 300,
      render: (val) => {
        // Si val es ID, buscar en catalogo, si es objeto mostrar descripcion
        if (typeof val === 'object') return val.nombre || val.descripcion || val.clave;
        const p = plazas.find((item) => item.id === val);
        return p ? `${p.clave || ''} - ${p.nombre || p.descripcion}` : val;
      },
    },
    {
      // Tipo de Nómina Empleado de la Plaza (no de quien la ocupa hoy): ayuda a detectar en
      // la tabla una configuración que nunca va a aplicarse, p.ej. cargar un importe aquí
      // para una Percepción ligada solo a Plantilla cuando la Plaza es de tipo Eventual.
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'plaza',
      key: 'tipo_nomina_empleado',
      width: 200,
      render: (val) => {
        const plazaId = typeof val === 'object' ? val?.id : val;
        const p = plazas.find((item) => item.id === plazaId);
        return p?.tipo_nomina_empleado || '';
      },
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      message.loading({
        content: 'Iniciando importación...',
        key: 'importarExcelImportePlaza',
        duration: 0,
      });
      const response = await API.post(
        `nominas/percepciones-importe-plaza-importar-excel/${percepcionId}/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      // El backend procesa en background y responde { task_id } (202) —
      // arrancamos el polling del proceso en vez de esperar la respuesta.
      if (response?.data?.task_id) {
        setIsPollingImport(true);
      } else {
        message.destroy('importarExcelImportePlaza');
      }
    } catch (error) {
      message.destroy('importarExcelImportePlaza');
      message.error('Error al importar el archivo. Verifica el formato.');
    } finally {
      e.target.value = null;
    }
  };

  // Polling via ProcesoEnCursoTerminado — consulta el avance cada 2s y, al
  // terminar, descarga el Excel de resultado (columna Estatus por fila).
  useEffect(() => {
    if (!isPollingImport) return () => {};

    let intentos = 0;
    const maxIntentos = 340; // máx ~11 min

    pollingImportRef.current = setInterval(async () => {
      try {
        intentos += 1;
        const lista = await getProcesoEnCursoTerminado({
          descripcion: descripcionProcesoImportar,
        });
        const proceso = lista[0];

        if (proceso) {
          message.loading({
            content: proceso.total_registros
              ? `Procesando ${proceso.registro_en_curso || 0} de ${proceso.total_registros}...`
              : 'Procesando importación...',
            key: 'importarExcelImportePlaza',
            duration: 0,
          });
        }

        const termino = proceso && proceso.procesando === false;
        if (termino || intentos >= maxIntentos) {
          clearInterval(pollingImportRef.current);
          pollingImportRef.current = null;
          setIsPollingImport(false);
          message.destroy('importarExcelImportePlaza');

          let huboErrores = false;
          if (proceso?.resultado) {
            const byteCharacters = atob(proceso.resultado);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i += 1) {
              byteArray[i] = byteCharacters.charCodeAt(i);
            }

            try {
              const workbook = XLSX.read(byteArray, { type: 'array' });
              const sheet = workbook.Sheets[workbook.SheetNames[0]];
              const filas = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
              huboErrores = filas.slice(1).some((fila) => {
                const estatus = fila[4];
                return estatus && String(estatus).trim() !== '' && estatus !== 'OK';
              });
            } catch (_err) {
              // si no se puede leer, igual se descarga el archivo
            }

            const blob = new Blob([byteArray], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Resultado_Importe_Plaza.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          }

          if (huboErrores) {
            notification.warning({
              message: 'Importación finalizada con errores',
              description: 'Se descargó el archivo de resultado. Revisa la columna Estatus para el detalle.',
              placement: 'topRight',
              duration: 8,
            });
          } else {
            notification.success({
              message: 'Importación completada',
              description: 'La carga de Importe por Plaza finalizó correctamente.',
              placement: 'topRight',
            });
          }

          // Recarga la tabla mostrando primero lo recién importado/cerrado,
          // y fuerza el remount de <Table> para que su paginación regrese a
          // la página 1 -- si no, el usuario se queda viendo la misma
          // página de antes y el import parece no haber hecho nada.
          await fetchData({ masRecientesPrimero: true });
          setTableRefreshKey((k) => k + 1);
        }
      } catch (_err) {
        // No se interrumpe el polling: el siguiente tick reintenta. Si el
        // error es por sesión expirada, utils/api.js ya avisó y cerró
        // sesión; aquí solo dejamos rastro para diagnóstico.
        // eslint-disable-next-line no-console
        console.error('[polling importación importe por plaza] error al consultar estado', _err);
      }
    }, 2000);

    return () => {
      if (pollingImportRef.current) {
        clearInterval(pollingImportRef.current);
        pollingImportRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPollingImport]);

  const handleDownloadLayout = () => {
    try {
      message.loading({ content: 'Generando layout...', key: 'descargaLayout' });

      // 1. Crear un nuevo libro de Excel (Workbook)
      const wb = XLSX.utils.book_new();

      // 2. Crear la hoja principal con los encabezados
      const encabezados = [['*PLAZA', '*IMPORTE', 'VIGENCIA INICIAL', 'VIGENCIA FINAL']];
      const wsPrincipal = XLSX.utils.aoa_to_sheet(encabezados);

      // Ajustar el ancho de las columnas (A: 80, B: 20, C: 18, D: 18)
      wsPrincipal['!cols'] = [{ wch: 80 }, { wch: 20 }, { wch: 18 }, { wch: 18 }];

      // Agregar la hoja principal al libro
      XLSX.utils.book_append_sheet(wb, wsPrincipal, 'Carga Masiva Plazas');

      // 3. Crear la hoja de Catálogos usando el estado "plazas" que ya tienes en React
      const encabezadoCatalogo = [['Catálogo de Plazas Disponibles (Copia y pega en la hoja principal)']];

      // Mapeamos el estado de plazas al formato que espera el Excel
      const datosPlazas = plazas.map((plaza) => {
        const nombrePlaza = plaza.descripcion || plaza.nombre || '';
        const clavePlaza = plaza.clave || '';
        return [`${clavePlaza} - ${nombrePlaza}`];
      });

      const wsCatalogos = XLSX.utils.aoa_to_sheet([...encabezadoCatalogo, ...datosPlazas]);

      // Ajustar ancho de la columna de catálogos
      wsCatalogos['!cols'] = [{ wch: 100 }];

      // Agregar la hoja de catálogos al libro
      XLSX.utils.book_append_sheet(wb, wsCatalogos, 'Catalogos');

      // 4. Generar y descargar el archivo directamente desde el navegador
      XLSX.writeFile(wb, 'Layout_Importe_Plaza.xlsx');

      message.success({ content: 'Layout descargado exitosamente', key: 'descargaLayout' });
    } catch (error) {
      message.error({ content: 'Error al generar el layout', key: 'descargaLayout' });
    }
  };

  // const handleDownloadLayout = async () => {
  //   try {
  //     message.loading({ content: 'Descargando layout...', key: 'descargaLayout' });
  // const response = await API.get('nominas/percepciones-importe-plaza-obtener-plantilla-excel/', {
  //       responseType: 'blob',
  //     });
  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', 'Layout_Importe_Plaza.xlsx');
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //     message.success({ content: 'Layout descargado exitosamente', key: 'descargaLayout' });
  //   } catch (error) {
  //     message.error({ content: 'Error al descargar el layout', key: 'descargaLayout' });
  //   }
  // };

  const requiredRule = { required: true, message: 'El campo es requerido' };

  return (
    <Row style={{ marginTop: 20 }}>
      <Spin tip="Cargando..." spinning={loading}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".xlsx, .xls"
          onChange={handleUploadExcel}
        />
        <Table
          key={tableRefreshKey}
          titleTable="Plazas Asociadas"
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          onTableChange={handleTableChange}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          disabled={disabled || !percepcionId}
          customActions={[
            {
              text: 'Descargar Layout',
              icon: <DownloadOutlined />,
              onClick: handleDownloadLayout,
              disabledBySelection: false, // <--- Evita que se bloquee si no hay selección
              disabled: false, // <--- Lo fuerza a estar activo siempre
            },
            {
              text: 'Importar Excel',
              icon: <UploadOutlined />,
              onClick: () => fileInputRef.current.click(),
              disabledBySelection: false, // <--- Evita que se bloquee si no hay selección
              disabled: false, // <--- Lo fuerza a estar activo siempre
            },
          ]}
        />

        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={800}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Importe por Plaza`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="formPlaza"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={16}>
              {/* Campo PLAZA */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="plaza"
                  label="Plaza"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    dataSource={plazas}
                    keyProp="id"
                    labelProp="clave_y_descripcion"
                    renderLabel={(item) => {
                      if (item.clave_y_descripcion) return item.clave_y_descripcion;
                      return `${item.clave || item.id} - ${item.descripcion || ''}`;
                    }}
                    showSearch
                    disabled={disableFields}
                    filterProp="clave_y_descripcion"
                  />
                </Form.Item>
              </Col>

              {/* Tipo de Nómina Empleado de la Plaza seleccionada -- solo lectura, informativo:
                  ayuda a notar antes de guardar si esta Plaza en realidad no es del tipo de
                  Nómina Empleado al que está ligada esta Percepción (nunca se aplicaría). */}
              <Col xs={24} md={12}>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.plaza !== curr.plaza}>
                  {({ getFieldValue }) => {
                    const plazaSeleccionada = plazas.find((item) => item.id === getFieldValue('plaza'));
                    return (
                      <Form.Item label="Tipo de Nómina Empleado">
                        <Input value={plazaSeleccionada?.tipo_nomina_empleado || ''} disabled />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>

              {/* Campo IMPORTE */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="importe"
                  label="Importe"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Input
                    prefix="$"
                    type="number"
                    step="0.01"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Campo VIGENCIA INICIAL */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="vigencia_inicial"
                  label="Vigencia Inicial"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Campo VIGENCIA FINAL */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  hasFeedback
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Campo ESTATUS */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0 || disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={selectedRowKeys.length > 1
            ? `${selectedRowKeys.length} registros de Plaza seleccionados`
            : '¿Estás seguro de eliminar este registro de Plaza?'}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionImportePlaza;
